import { AnalyticsService } from '@alison/analytics/client';
import ArrowDownwardOutlined from '@mui/icons-material/ArrowDownwardOutlined';
import ArrowUpwardOutlined from '@mui/icons-material/ArrowUpwardOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { ReactNode } from 'react';
import type { MetricCatalog } from './data/api';
import {
  ACTIVE_DAYS_SORT_FIELD,
  CREATIVE_ID_SORT_FIELD,
  CREATIVE_SCORE_METRIC_NAME,
  CTR_METRIC_NAME,
  type DisplayedMetrics,
  SPEND_RATIO_SORT_FIELD,
  setSortField,
} from './data/displayedMetrics';
import { TOOLBAR_ICON_BUTTON_SX } from './utils';

// Alison 1.0's sort menu, in its order; a metric shows only when the account's catalog has it.
const SPEND_METRIC_NAME = 'cost';
const HOOK_SCORE_METRIC_NAME = 'hook_score';
const ROAS_METRIC_NAME = 'roas';
const FIELD_LABELS: Record<string, string> = {
  [ACTIVE_DAYS_SORT_FIELD]: 'Active Days',
  [SPEND_RATIO_SORT_FIELD]: 'Spend Ratio',
  [CREATIVE_ID_SORT_FIELD]: 'Creative',
};

interface SortOption {
  field: string;
  label: string;
}

interface SortControlProps {
  catalog: MetricCatalog | undefined;
  applied: DisplayedMetrics;
  leadingKpi: string;
  leadingKpiLabel: string;
  onChange: (next: DisplayedMetrics) => void;
}

export function sortOptions(
  catalog: MetricCatalog | undefined,
  leadingKpi: string,
  leadingKpiLabel: string,
  current: string,
): SortOption[] {
  const metric = (name: string, label?: string): SortOption[] => {
    const entry = catalog?.[name];
    return entry?.selectable ? [{ field: name, label: label ?? entry.label }] : [];
  };
  const options = [
    ...metric(SPEND_METRIC_NAME),
    // As in Alison 1.0, ROAS takes the leading KPI's slot when that slot would repeat CTR.
    ...(leadingKpi === CTR_METRIC_NAME
      ? metric(ROAS_METRIC_NAME)
      : metric(leadingKpi, leadingKpiLabel)),
    ...metric(CTR_METRIC_NAME),
    ...metric(HOOK_SCORE_METRIC_NAME),
    ...metric(CREATIVE_SCORE_METRIC_NAME),
    { field: ACTIVE_DAYS_SORT_FIELD, label: FIELD_LABELS[ACTIVE_DAYS_SORT_FIELD] ?? '' },
    { field: SPEND_RATIO_SORT_FIELD, label: FIELD_LABELS[SPEND_RATIO_SORT_FIELD] ?? '' },
  ].filter((option, index, all) => all.findIndex((o) => o.field === option.field) === index);
  if (current === '' || options.some((option) => option.field === current)) return options;
  return [
    ...options,
    { field: current, label: FIELD_LABELS[current] ?? catalog?.[current]?.label ?? current },
  ];
}

export function sortLabel(option: SortOption | undefined, direction: 'asc' | 'desc'): string {
  if (option === undefined) return 'Sort';
  if (option.field === ACTIVE_DAYS_SORT_FIELD) {
    return `${direction === 'desc' ? 'Most' : 'Least'} ${option.label}`;
  }
  return `${direction === 'desc' ? 'Highest' : 'Lowest'} ${option.label}`;
}

export function SortControl({
  catalog,
  applied,
  leadingKpi,
  leadingKpiLabel,
  onChange,
}: SortControlProps): ReactNode {
  const options = sortOptions(catalog, leadingKpi, leadingKpiLabel, applied.sort);
  const selected = options.find((option) => option.field === applied.sort);
  const flipped = applied.direction === 'desc' ? 'asc' : 'desc';

  const handleFieldChange = (field: string): void => {
    const next = setSortField(applied, catalog, field);
    AnalyticsService.track('Explore - Sort', { field, direction: next.direction });
    onChange(next);
  };

  const handleFlip = (): void => {
    AnalyticsService.track('Explore - Sort', { field: applied.sort, direction: flipped });
    onChange({ ...applied, direction: flipped });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Select
        size="small"
        value={selected ? applied.sort : ''}
        displayEmpty
        disabled={catalog === undefined}
        data-test="creatives-sort"
        inputProps={{ 'aria-label': 'Sort creatives by' }}
        renderValue={() => sortLabel(selected, applied.direction)}
        onChange={(event) => handleFieldChange(event.target.value)}
        sx={{ height: 30, fontSize: 12, fontWeight: 600, minWidth: 150 }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.field}
            value={option.field}
            data-test={`creatives-sort-option-${option.field}`}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
      <IconButton
        size="small"
        aria-label={sortLabel(selected, flipped)}
        title={sortLabel(selected, flipped)}
        data-test="creatives-sort-direction"
        disabled={catalog === undefined || selected === undefined}
        onClick={handleFlip}
        sx={TOOLBAR_ICON_BUTTON_SX}
      >
        {applied.direction === 'desc' ? (
          <ArrowDownwardOutlined sx={{ fontSize: 16 }} />
        ) : (
          <ArrowUpwardOutlined sx={{ fontSize: 16 }} />
        )}
      </IconButton>
    </Box>
  );
}
